import { createSignal, onMount, For, Show } from 'solid-js';
import { createStore, reconcile } from 'solid-js/store';
import { A } from '@solidjs/router';
import { useApi, isOk } from '../api';
import { useToast } from '../components/MessageBox';
import { showConfirm } from '../components/Dialog';
import { useTranslation } from '../hooks/useTranslation';
import { formatDateTime } from '../services/dateFormat';
import { EmptyState } from '../components/EmptyState';
import Text from '../components/Text';
import type { SchematicSummaryDto } from '../api/generated/schemas';
import styles from './Schematics.module.css';

export default function Schematics() {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();

    const [items, setItems] = createStore<SchematicSummaryDto[]>([]);
    const [loading, setLoading] = createSignal(true);
    const [deletingId, setDeletingId] = createSignal<string | null>(null);

    const load = async () => {
        const res = await api.schematics.list();
        if (isOk(res.data)) {
            setItems(reconcile((res.data.value.schematics ?? []) as unknown as SchematicSummaryDto[], { key: 'id' }));
        } else {
            toast?.error(t('SCHEMATICS_LOAD_ERROR'));
        }
        setLoading(false);
    };

    onMount(() => void load());

    const remove = async (s: SchematicSummaryDto) => {
        if (!(await showConfirm(t('SCHEMATICS_DELETE_CONFIRM', s.name), true))) return;
        setDeletingId(s.id);
        const res = await api.schematics.delete(s.id);
        setDeletingId(null);
        if (isOk(res.data)) {
            setItems(items.filter(i => i.id !== s.id));
            toast?.success(t('SCHEMATICS_DELETED'));
        } else {
            toast?.error(t('SCHEMATICS_DELETE_ERROR'));
        }
    };

    return (
        <div class={styles.container}>
            <div class={styles.headerRow}>
                <h1><Text val="SCHEMATICS_TITLE" /></h1>
                <Show when={!loading()}>
                    <span class={styles.entryCount}>{items.length} <Text val="SCHEMATICS_COUNT" /></span>
                </Show>
            </div>
            <p class={styles.subtitle}><Text val="SCHEMATICS_SUBTITLE" /></p>

            <Show when={!loading()} fallback={
                <div class={styles.list}>
                    <For each={[0, 1, 2]}>{() => <div class={styles.skeletonCard} />}</For>
                </div>
            }>
                <Show when={items.length > 0} fallback={
                    <EmptyState illustration="generic" title={t('SCHEMATICS_EMPTY_TITLE')} hint={t('SCHEMATICS_EMPTY_HINT')} />
                }>
                    <div class={styles.list}>
                        <For each={items}>{(s) => (
                            <div class="fcard">
                                <div class={styles.rowTop}>
                                    <div class={styles.name}>
                                        <i class="fa-solid fa-cubes" />
                                        <span>{s.name}</span>
                                    </div>
                                    <div class="h" style={{ gap: 'var(--spacing-sm)' }}>
                                        <A href={`/schematics/${s.id}/build`} class="btn btn-primary btn-sm">
                                            <i class="fa-solid fa-hammer" /> <Text val="SCHEMATICS_BUILD" />
                                        </A>
                                        <button
                                            class="btn btn-danger btn-sm"
                                            disabled={deletingId() === s.id}
                                            title={t('DELETE')}
                                            onClick={() => void remove(s)}
                                        >
                                            <Show when={deletingId() === s.id} fallback={<i class="fa-solid fa-trash" />}>
                                                <i class="fa-solid fa-spinner fa-spin" />
                                            </Show>
                                        </button>
                                    </div>
                                </div>
                                <div class={styles.fields}>
                                    <div class={styles.field}>
                                        <span class={styles.fieldLabel}><Text val="SCHEMATICS_SIZE" /></span>
                                        <span class={styles.fieldValue}>{s.width} x {s.height} x {s.length}</span>
                                    </div>
                                    <div class={styles.field}>
                                        <span class={styles.fieldLabel}><Text val="SCHEMATICS_ANCHOR" /></span>
                                        <span class={styles.fieldValue}>{s.anchorX}, {s.anchorY}, {s.anchorZ}</span>
                                    </div>
                                    <div class={styles.field}>
                                        <span class={styles.fieldLabel}><Text val="SCHEMATICS_CITYBUILD" /></span>
                                        <span class={styles.fieldValue}>{s.citybuildName}</span>
                                    </div>
                                    <Show when={s.location}>
                                        <div class={styles.field}>
                                            <span class={styles.fieldLabel}><Text val="SCHEMATICS_LOCATION" /></span>
                                            <span class={styles.fieldValue}>{s.location}</span>
                                        </div>
                                    </Show>
                                    <div class={styles.field}>
                                        <span class={styles.fieldLabel}><Text val="SCHEMATICS_CREATED" /></span>
                                        <span class={styles.fieldValue}>{formatDateTime(s.createdAt)}</span>
                                    </div>
                                </div>
                            </div>
                        )}</For>
                    </div>
                </Show>
            </Show>
        </div>
    );
}
